/**
 * The organization's custom rubric: its own metrics and categories.
 *
 * Before this a custom metric could only be created — one at a time, from the
 * Metrics step of an import — and a mistake was permanent. Here they can be
 * reviewed, edited, disabled or deleted, and a whole rubric (or the customer's
 * KPI data file itself, which repeats each metric's definition on every row)
 * can be loaded at once, after a preview of exactly what will change.
 *
 * Standard metrics are not edited here: the platform catalog wins, and the
 * import reports them rather than changing them.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { Download, Pencil, Trash2, Upload } from "lucide-react";

import { AddCustomDialog, type MetricChoices } from "./AddCustomDialog";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  deleteDataHubCustomCategory,
  deleteDataHubCustomMetric,
  fetchDataHubCustomRubric,
  fetchDataHubMetricDefinitions,
  importDataHubCustomRubric,
  updateDataHubCustomCategory,
  updateDataHubCustomMetric,
} from "../../Store/Reducers/DataHubConsoleSlice";
import {
  describeError,
  type CategoryOption,
  type CustomMetric,
  type RubricCategory,
  type RubricImportPlanEntry,
} from "../data/api";
import {
  DEFAULT_FORMATS,
  DEFAULT_SCALES,
  mergeChoices,
} from "../lib/custom-metric-seed";
import { parseSheet } from "../lib/sheet-to-csv";

const TEMPLATE_HEADERS = [
  "Metric ID",
  "Metric Name",
  "Category ID",
  "Category",
  "Sub-Category",
  "UOM",
  "Format",
  "Scale",
  "Normalization Type",
  "Normalization Direction",
  "Description",
  "Business Impact",
  "Formula",
];

const ACTION_STYLE: Record<RubricImportPlanEntry["action"], string> = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  unchanged: "bg-gray-100 text-gray-600",
  standard: "bg-gray-100 text-gray-600",
  error: "bg-red-100 text-red-800",
};

function downloadTemplate() {
  const csv = `${TEMPLATE_HEADERS.join(",")}\n`;
  const href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = href;
  a.download = "custom-rubric-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

export function CustomRubricPanel() {
  const dispatch = useAppDispatch();
  const [metrics, setMetrics] = useState<CustomMetric[] | null>(null);
  const [categories, setCategories] = useState<RubricCategory[]>([]);
  const [choices, setChoices] = useState<MetricChoices>({
    units: [],
    formats: [],
    scales: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<CustomMetric | null>(null);
  /** Delete asks twice: the second click on the same row confirms. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<RubricCategory | null>(
    null
  );
  const [plan, setPlan] = useState<{
    fileName: string;
    rows: Record<string, string>[];
    entries: RubricImportPlanEntry[];
    summary: Record<string, number>;
  } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [rubric, defs] = await Promise.all([
        dispatch(fetchDataHubCustomRubric()).unwrap(),
        dispatch(fetchDataHubMetricDefinitions()).unwrap(),
      ]);
      setMetrics(rubric.metrics);
      setCategories(rubric.categories);
      setChoices({
        units: mergeChoices(
          defs.metrics.map(m => m.unit_of_measure),
          []
        ),
        formats: mergeChoices(
          defs.metrics.map(m => m.format),
          DEFAULT_FORMATS
        ),
        scales: mergeChoices(
          defs.metrics.map(m => m.scale),
          DEFAULT_SCALES
        ),
      });
    } catch (err: any) {
      setError(describeError(err));
      setMetrics([]);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const categoryOptions: CategoryOption[] = useMemo(
    () =>
      categories
        .filter(c => c.enabled)
        .map(c => ({
          key: c.category_key,
          label: c.label ?? c.category_key,
          long_label: c.long_label,
        })),
    [categories]
  );
  const labelFor = (key: string | null | undefined) => {
    const c = categories.find(x => x.category_key === key);
    return c ? `${c.long_label || c.label} (${c.category_key})` : key || "—";
  };
  const customCategories = categories.filter(c => c.source === "custom");

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err: any) {
      setError(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy("plan");
    try {
      const table = await parseSheet(file);
      const res = await dispatch(
        importDataHubCustomRubric({ rows: table.rows, apply: false })
      ).unwrap();
      setPlan({
        fileName: file.name,
        rows: table.rows,
        entries: res.plan,
        summary: res.summary,
      });
    } catch (err: any) {
      setError(describeError(err));
    } finally {
      setBusy(null);
    }
  };

  const applyPlan = async () => {
    if (!plan) return;
    await run("apply", async () => {
      await dispatch(
        importDataHubCustomRubric({ rows: plan.rows, apply: true })
      ).unwrap();
      setPlan(null);
    });
  };

  const toApply = plan
    ? (plan.summary.create ?? 0) + (plan.summary.update ?? 0)
    : 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="m-0 text-lg font-bold text-gray-900">Custom rubric</h2>
          <p className="m-0 max-w-3xl text-sm text-gray-500">
            Metrics and categories that exist for this organization only. Load a
            rubric file — or your KPI data file itself — to add them all at
            once, then edit, disable or delete any of them. Standard metrics
            come from the platform catalog and are not changed here.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline-primary"
            onClick={downloadTemplate}
            icon={<Download className="h-4 w-4" />}
          >
            Template
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700">
            <Upload className="h-4 w-4" />
            {busy === "plan" ? "Reading…" : "Upload rubric CSV"}
            <input
              aria-label="Upload rubric file"
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={e => {
                void onFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className="space-y-2">
        <h3 className="m-0 text-sm font-semibold text-gray-800">
          Custom metrics ({metrics?.length ?? "…"})
        </h3>
        <div className="max-h-[420px] overflow-auto rounded-lg border border-gray-200">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Metric</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Unit · format · scale</th>
                <th className="px-3 py-2">Direction</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {metrics === null ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              ) : metrics.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-gray-500"
                  >
                    No custom metrics yet. Upload a rubric or add them from an
                    import&apos;s Metrics step.
                  </td>
                </tr>
              ) : (
                metrics.map(m => {
                  const disabled = m.enabled === false;
                  return (
                    <tr
                      key={m.metric_id}
                      className={`border-t border-gray-100 align-top ${
                        disabled ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">
                          {m.metric_name}
                        </div>
                        <div className="font-mono text-xs text-gray-500">
                          {m.metric_id}
                          {disabled && " · disabled"}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {labelFor(m.category_key)}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {[m.unit_of_measure, m.format, m.scale]
                          .filter(Boolean)
                          .join(" · ")}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {m.normalization_direction === "lower_better"
                          ? "Lower is better"
                          : "Higher is better"}
                      </td>
                      <td className="max-w-[320px] px-3 py-2 text-gray-600">
                        {m.description ? (
                          <span className="line-clamp-2">{m.description}</span>
                        ) : (
                          <span className="text-amber-700">
                            No description — the KPI Briefing needs one
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="clear"
                            className="h-8 px-2 text-xs"
                            onClick={() => setEditing(m)}
                            icon={<Pencil className="h-3.5 w-3.5" />}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="clear"
                            className="h-8 px-2 text-xs"
                            disabled={busy === m.metric_id}
                            onClick={() =>
                              run(m.metric_id, () =>
                                dispatch(
                                  updateDataHubCustomMetric({
                                    metricId: m.metric_id,
                                    changes: { enabled: disabled },
                                  })
                                ).unwrap()
                              )
                            }
                          >
                            {disabled ? "Enable" : "Disable"}
                          </Button>
                          <Button
                            variant="clear"
                            className="h-8 px-2 text-xs text-red-600 hover:text-red-800"
                            disabled={busy === m.metric_id}
                            onClick={() => {
                              if (confirming !== m.metric_id) {
                                setConfirming(m.metric_id);
                                return;
                              }
                              setConfirming(null);
                              void run(m.metric_id, () =>
                                dispatch(
                                  deleteDataHubCustomMetric(m.metric_id)
                                ).unwrap()
                              );
                            }}
                            icon={<Trash2 className="h-3.5 w-3.5" />}
                          >
                            {confirming === m.metric_id
                              ? "Confirm delete"
                              : "Delete"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="m-0 text-sm font-semibold text-gray-800">
          Custom categories ({customCategories.length})
        </h3>
        {customCategories.length === 0 ? (
          <p className="m-0 text-sm text-gray-500">
            None. The standard categories are:{" "}
            {categories
              .filter(c => c.source !== "custom")
              .map(c => `${c.long_label || c.label} (${c.category_key})`)
              .join(", ")}
            .
          </p>
        ) : (
          <div className="overflow-auto rounded-lg border border-gray-200">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {customCategories.map(c => (
                  <tr key={c.category_key} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">
                        {c.long_label || c.label}
                      </div>
                      <div className="font-mono text-xs text-gray-500">
                        {c.category_key} · {c.custom_metric_count} custom metric
                        {c.custom_metric_count === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="clear"
                          className="h-8 px-2 text-xs"
                          onClick={() => setEditingCategory(c)}
                          icon={<Pencil className="h-3.5 w-3.5" />}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="clear"
                          className="h-8 px-2 text-xs text-red-600 hover:text-red-800"
                          disabled={busy === c.category_key}
                          onClick={() => {
                            if (confirming !== c.category_key) {
                              setConfirming(c.category_key);
                              return;
                            }
                            setConfirming(null);
                            void run(c.category_key, () =>
                              dispatch(
                                deleteDataHubCustomCategory(c.category_key)
                              ).unwrap()
                            );
                          }}
                          icon={<Trash2 className="h-3.5 w-3.5" />}
                        >
                          {confirming === c.category_key
                            ? "Confirm delete"
                            : "Delete"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <AddCustomDialog
          kind="metric"
          initialName={editing.metric_id}
          categories={categoryOptions}
          choices={choices}
          editing={editing}
          open
          onOpenChange={o => {
            if (!o) setEditing(null);
          }}
          onCreated={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {editingCategory && (
        <CategoryEditDialog
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={changes =>
            run(editingCategory.category_key, async () => {
              await dispatch(
                updateDataHubCustomCategory({
                  key: editingCategory.category_key,
                  changes,
                })
              ).unwrap();
              setEditingCategory(null);
            })
          }
        />
      )}

      {plan && (
        <CommonModal
          isOpen
          toggle={() => setPlan(null)}
          title="Review the rubric import"
          subtitle={`${plan.fileName} · nothing is saved until you apply`}
          size="4xl"
          usePortal
          footer={
            <>
              <Button variant="outline-primary" onClick={() => setPlan(null)}>
                Cancel
              </Button>
              <Button
                onClick={applyPlan}
                disabled={toApply === 0 || busy === "apply"}
                loading={busy === "apply"}
              >
                Apply {toApply} change{toApply === 1 ? "" : "s"}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm">
              {(["create", "update", "unchanged", "standard", "error"] as const)
                .filter(a => plan.summary[a])
                .map(a => (
                  <span
                    key={a}
                    className={`rounded-md px-2.5 py-1 font-medium ${ACTION_STYLE[a]}`}
                  >
                    {plan.summary[a]} {a === "error" ? "need fixing" : a}
                  </span>
                ))}
            </div>
            {(plan.summary.error ?? 0) > 0 && (
              <p className="m-0 text-sm text-red-700">
                Rows that need fixing are left out when you apply. Fix them in
                the file and upload it again.
              </p>
            )}
            <div className="max-h-[50vh] overflow-auto rounded-lg border border-gray-200">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Metric</th>
                    <th className="px-3 py-2">Result</th>
                    <th className="px-3 py-2">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.entries.map(e => (
                    <tr key={e.metric_id} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <div className="text-gray-900">{e.metric_name}</div>
                        <div className="font-mono text-xs text-gray-500">
                          {e.metric_id}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-semibold ${ACTION_STYLE[e.action]}`}
                        >
                          {e.action}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {e.reason ??
                          (e.changes?.length
                            ? `changes ${e.changes.join(", ")}`
                            : e.category_key
                              ? labelFor(e.category_key)
                              : "")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CommonModal>
      )}
    </div>
  );
}

function CategoryEditDialog({
  category,
  onClose,
  onSave,
}: {
  category: RubricCategory;
  onClose: () => void;
  onSave: (changes: {
    label: string;
    long_label: string;
    description: string;
  }) => void;
}) {
  const [label, setLabel] = useState(category.label ?? "");
  const [longLabel, setLongLabel] = useState(category.long_label ?? "");
  const [description, setDescription] = useState(category.description ?? "");
  const field =
    "h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-purple-500 focus:outline-none";
  return (
    <CommonModal
      isOpen
      toggle={onClose}
      title="Edit custom category"
      subtitle={`Id ${category.category_key} cannot change: metrics and scores are keyed on it.`}
      size="lg"
      usePortal
      footer={
        <>
          <Button variant="outline-primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!label.trim()}
            onClick={() =>
              onSave({
                label: label.trim(),
                long_label: longLabel.trim(),
                description: description.trim(),
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block space-y-1 text-sm font-medium text-gray-700">
          Name
          <input
            className={field}
            value={label}
            onChange={e => setLabel(e.target.value)}
          />
        </label>
        <label className="block space-y-1 text-sm font-medium text-gray-700">
          Long name
          <input
            className={field}
            value={longLabel}
            onChange={e => setLongLabel(e.target.value)}
          />
        </label>
        <label className="block space-y-1 text-sm font-medium text-gray-700">
          Description
          <textarea
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none"
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </label>
      </div>
    </CommonModal>
  );
}

export default CustomRubricPanel;
